import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { StyledIcon, SvgSrc } from '~/components/shared/Icon/Icon';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import useModals from '~/hooks/useModals';
import { OwnerAmountEnum } from '~/enums';
import { IOwner, OwnerApiInterface } from '~/Interfaces';
import { addBeneficialOwners } from '~/store/actions/invoiceActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '~/store/types/sharedTypes';
import useTranslate from '~/hooks/useTranslate';
import PaymentMethodsHeader from '../PaymentMethodsHeader';
import { DoneButton } from '../PaymentConfirmation';
import {
  PaymentMethodBodyContainer,
  PaymentMethodHeaderContainer,
  PaymentMethodMainContainer,
  StepCircle,
} from '../../../Invoice';
import { TryAgainContainer } from '../../SomethingWentWrong';
import Owner from './Owner';
import { ErrorMessageIcon, PaymentRow } from '../Instalments/InstalmentCards';

const GrayText = styled.div`
  display: flex;
  justify-content: left;
`;
export const CompanyOwnersContainer = styled.div`
  width: 100%;
  padding: 20px 40px 20px 40px;
  background-color: white;
  border: 2px solid #f2f3f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const CompanyOwnersHeader = styled.span`
  text-align: left;
  font-size: 16px;
  color: #444444;
`;
const Owners = styled.div`
  padding: 10px 0px 10px 0px;
  border-bottom: 2px solid #e9e9e9;
`;
const AddOwnerButton = styled.button`
  width: 100%;
  color: #214749;
  background-color: #F8F9F5
  border: none;
  border-radius: 10px;
  height: 47px;
  margin: auto;
  max-width: 310px;
  margin-top: 10px;
  font-weight: 600
  font-size: 16px;
  `;

interface Props {
  onCancelButton: () => void;
  onAddOwners: (owners?: OwnerApiInterface[]) => void;
}

const Ubo: React.FC<Props> = (props) => {
  const { onCancelButton, onAddOwners } = props;
  const modals = useModals();
  const dispatch = useDispatch();
  const translate = useTranslate();

  const { singleInvoice } = useSelector((state: RootState) => state.factoring);

  const [owners, setOwners] = useState<IOwner[]>([]);

  const adjustExtentCode = (amount: string): string => {
    if (amount === OwnerAmountEnum.TwentyFiveTo50) {
      return 'INTERVALL2';
    }
    if (amount === OwnerAmountEnum.FiftyTo75) {
      return 'INTERVALL3';
    }
    if (amount === OwnerAmountEnum.SeventyFiveTo100) {
      return 'INTERVALL4';
    }
    return 'INTERVALL5';
  };

  useEffect(() => {
    if (singleInvoice.debtor.beneficialOwners) {
      const helperOwnersArray: IOwner[] = singleInvoice.debtor.beneficialOwners.map(
        (owner) => ({
          name: owner.name,
          email: owner.email,
          personalNumber: owner.personalNo,
          amount: owner.ownPercent ? owner.ownPercent : '',
        })
      );
      setOwners(helperOwnersArray);
    }
  }, [singleInvoice]);

  const confirm = () => {
    const apiOwners: OwnerApiInterface[] = [];
    owners.forEach((owner) => {
      apiOwners.push({
        name: owner.name,
        email: owner.email,
        personalNo: owner.personalNumber,
        ownerExtentCode: adjustExtentCode(owner.amount),
      });
    });
    onAddOwners(apiOwners);
    dispatch(addBeneficialOwners(singleInvoice.debtor.orgNumber, apiOwners));
  };

  const onOwnerSave = (owner: IOwner, index: number) => {
    if (index !== undefined) {
      const editedOwners = owners;
      editedOwners[index] = owner;
      setOwners(editedOwners);
    } else {
      setOwners([...owners, owner]);
    }
    modals.closeModal('owner-modal');
  };

  const handleOpen = (ownerToEdit?: IOwner, key?: number) => {
    modals.openModal({
      id: 'owner-modal',
      header: translate('CreditCardCompanyOwnersCard.AddOwner'),
      size: 'xs',
      children: <Owner owner={ownerToEdit} index={key} onSave={onOwnerSave} />,
      heightAuto: true,
    });
  };

  const onDeleteEditConfirm = (
    deleteOrEdit: boolean,
    key: number,
    ownerToEdit?: IOwner
  ) => {
    if (deleteOrEdit) {
      owners.splice(key, 1);
    } else {
      setTimeout(() => {
        handleOpen(ownerToEdit, key);
      }, 200);
    }
  };

  const openConfirmModal = (
    deleteOrEdit: boolean,
    ownerToEdit: IOwner,
    key: number
  ) => {
    modals.openConfirmModal({
      header: deleteOrEdit ? translate('Invoices.Delete') : translate('Edit'),
      size: 'sm',
      onConfirm: () => onDeleteEditConfirm(deleteOrEdit, key, ownerToEdit),
      labels: {
        confirm: deleteOrEdit
          ? translate('Invoices.Delete')
          : translate('Edit'),
        cancel: translate('Cancel'),
      },
      children: (
        <Typography centerAlign fontSize={0.7}>
          {deleteOrEdit
            ? translate('Invoices.DeleteBeneficialOwnerMessage')
            : translate('Invoices.EditBeneficialOwnerMessage')}
        </Typography>
      ),
      heightAuto: true,
    });
  };

  return (
    <PaymentMethodMainContainer>
      <PaymentMethodsHeader
        text={translate('Back').toString()}
        onCancelButton={onCancelButton}
      />
      <PaymentMethodBodyContainer>
        <PaymentMethodHeaderContainer withSubtext>
          <Typography
            lineHeight={1.8}
            fontSize={1.2}
            color="#444444"
            fontWeight={FontWeights.Bold}
          >
            <StepCircle green />
            <StepCircle green />
            <StepCircle />
            <StepCircle />
            <br />
            {translate('Invoices.VerifyOwnership')}
          </Typography>
          <Typography color="#444444" fontWeight={FontWeights.Normal}>
            {translate('Invoices.VerifyOwnershipSubtext')}
          </Typography>
        </PaymentMethodHeaderContainer>
        <CompanyOwnersContainer>
          <CompanyOwnersHeader>
            {translate('CompanyOwnersCard.Title')}
          </CompanyOwnersHeader>
          {owners.map((owner, key) => (
            // eslint-disable-next-line react/no-array-index-key
            <Owners key={key}>
              <PaymentRow>
                <Typography
                  fontWeight={FontWeights.Bold}
                  style={{ flex: 1, fontSize: '16px' }}
                >
                  {owner.name}
                </Typography>
                <Typography
                  fontWeight={FontWeights.Bold}
                  style={{ float: 'right', fontSize: '16px' }}
                >
                  {owner.amount}
                </Typography>
              </PaymentRow>
              <CompanyOwnersHeader style={{ fontSize: '14px' }}>
                {owner.personalNumber}
              </CompanyOwnersHeader>
              <PaymentRow>
                <Typography
                  fontSize={0.7}
                  fontWeight={FontWeights.Normal}
                  style={{ flex: 1, fontSize: '14px' }}
                >
                  {owner.email}
                </Typography>
                <ErrorMessageIcon
                  style={{ cursor: 'pointer' }}
                  onClick={() => openConfirmModal(false, owner, key)}
                >
                  <StyledIcon color="#1387AC" icon={SvgSrc.PenToSquare} />
                </ErrorMessageIcon>
                <ErrorMessageIcon
                  style={{ marginLeft: '20px', cursor: 'pointer' }}
                  onClick={() => openConfirmModal(true, owner, key)}
                >
                  <StyledIcon color="#444444" icon={SvgSrc.Trash} />
                </ErrorMessageIcon>
              </PaymentRow>
            </Owners>
          ))}
          <AddOwnerButton onClick={() => handleOpen()}>
            <StyledIcon
              style={{ marginRight: '10px', height: '16px' }}
              color="#444444"
              icon={SvgSrc.Plus}
            />
            {translate('Invoices.AddAditionalOwner')}
          </AddOwnerButton>
        </CompanyOwnersContainer>

        <TryAgainContainer>
          <DoneButton onClick={confirm}>
            {translate('Invoices.Confirm')}
          </DoneButton>
        </TryAgainContainer>
      </PaymentMethodBodyContainer>
    </PaymentMethodMainContainer>
  );
};

export default Ubo;
